import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { formatDistanceToNow } from "date-fns";
import classNames from "classnames";
import { MentionsInput, Mention } from "react-mentions";

import ProfileIcon from "$components/profile-icon";
import { extractUserTags, replaceMentionsWithLinks, buildMentionSuggestions } from "$components/comments/libs/helpers";
import defaultStyle from "$components/comments/default-style";
import api from "$services/api";
import { initiativesApi } from "../api";
import { setActivityCount } from "$stores/initiative-details";

// Treat API timestamps (no Z suffix) as UTC, then show relative local time
const parseUtc = (str) => {
    if (!str) return null;
    const normalized = /[Zz]|[+-]\d{2}:?\d{2}$/.test(str) ? str : `${str}Z`;
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
};

const formatAgo = (dateStr) => {
    const d = parseUtc(dateStr);
    return d ? formatDistanceToNow(d, { addSuffix: true }) : "";
};

// ─── Activity Feed ────────────────────────────────────────────────────────────

const ActivityMessage = ({ item }) => (
    <div className="comment">
        <ProfileIcon name={item.userName} modifierClassNames={["-small"]} />
        <div className="content">
            <div className="name-date">
                <b>{item.userName}</b>
                <span className="txt -small -light">{formatAgo(item.createdDate)}</span>
            </div>
            <p dangerouslySetInnerHTML={{ __html: replaceMentionsWithLinks(item.text) }} />
        </div>
    </div>
);

// Extract a trailing URL from system event text (e.g. "... — https://...")
const splitTextUrl = (text) => {
    const match = text.match(/^(.*?)\s*—\s*(https?:\/\/\S+)$/);
    if (match) return { label: match[1], url: match[2] };
    return { label: text, url: null };
};

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <button
            className="button -text -xsmall activity-copy-btn"
            onClick={copy}
            title="Copy link"
        >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
        </button>
    );
};

const SystemEvent = ({ item }) => {
    const { label, url } = splitTextUrl(item.text);
    return (
        <div className="activity-system-event">
            <span className="activity-system-event__text">
                <b>{item.userName}</b> — {label}
                {url && (
                    <>
                        {" "}
                        <a href={url} target="_blank" rel="noreferrer" className="txt -xsmall">
                            View link
                        </a>
                        <CopyButton text={url} />
                    </>
                )}
            </span>
            <span className="activity-system-event__date txt -small -light">
                {formatAgo(item.createdDate)}
            </span>
        </div>
    );
};

const ActivityFeed = ({ initiativeId }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mentionText, setMentionText] = useState("");
    const [collaborators, setCollaborators] = useState([]);
    const [posting, setPosting] = useState(false);
    const [postError, setPostError] = useState(null);
    const bottomRef = useRef(null);

    const load = (signal) => {
        initiativesApi
            .getActivity(initiativeId, signal)
            .then((res) => {
                if (signal?.aborted) return;
                const data = res || [];
                const sorted = [...data].sort(
                    (a, b) => new Date(a.createdDate) - new Date(b.createdDate)
                );
                setItems(sorted);
                setActivityCount(data.length);
                setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
            })
            .finally(() => {
                if (!signal?.aborted) setLoading(false);
            });
    };

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => controller.abort();
    }, [initiativeId]);

    useEffect(() => {
        const controller = new AbortController();
        initiativesApi
            .getCollaborators(initiativeId, controller.signal)
            .then((res) => {
                if (!controller.signal.aborted) setCollaborators(res || []);
            })
            .catch((err) => console.error("[ActivityTab] getCollaborators failed:", err));
        return () => controller.abort();
    }, [initiativeId]);

    const fetchMentionSuggestions = (query, callback) => {
        const abortController = new AbortController();
        const collaboratorResults = buildMentionSuggestions(query, collaborators, []);

        if (query.length >= 3) {
            api.get(`/api/employees/sharable?query=${encodeURIComponent(query)}`, null, { signal: abortController.signal })
                .then((res) => {
                    const searchResults = (res?.employees || []).map((u) => ({
                        display: u.fullName,
                        id: `${u.employeeId}|${u.userId}`,
                    }));
                    callback(buildMentionSuggestions(query, collaborators, searchResults));
                })
                .catch((err) => {
                    console.error("[ActivityTab] fetchMentionSuggestions failed:", err);
                    callback(collaboratorResults);
                });
        } else {
            callback(collaboratorResults);
        }

        return abortController;
    };

    const handlePost = async () => {
        if (!mentionText.trim()) return;
        setPosting(true);
        setPostError(null);
        try {
            const tags = extractUserTags(mentionText.trim());
            const collaboratorUserIds = new Set(collaborators.map((c) => c.userId));
            const nonCollaboratorTags = tags.filter((t) => !collaboratorUserIds.has(t.userId));

            if (nonCollaboratorTags.length > 0) {
                await Promise.all(
                    nonCollaboratorTags.map((t) => initiativesApi.addCollaborator(initiativeId, t.userId))
                );
                setCollaborators((prev) => [
                    ...prev,
                    ...nonCollaboratorTags.map((t) => ({ userId: t.userId, employeeId: t.employeeId, userName: t.userTag })),
                ]);
            }

            await initiativesApi.addComment(initiativeId, mentionText.trim(), mentionText.trim());
            setMentionText("");
            load(null);
        } catch {
            setPostError("Failed to post comment. Please try again.");
        } finally {
            setPosting(false);
        }
    };

    const hasItems = items.length > 0;

    return (
        <div className="comments-wrapper">
            <div className={classNames("comment-list", { "-no-comments": !hasItems && !loading })}>
                {!loading && !hasItems && <h5>No Activity Yet</h5>}
                {items.map((item) =>
                    item.eventType === "system" ? (
                        <SystemEvent key={item.id} item={item} />
                    ) : (
                        <ActivityMessage key={item.id} item={item} />
                    )
                )}
                <div ref={bottomRef} />
            </div>

            <div className="add-comment">
                {postError && <div className="txt -small -error">{postError}</div>}
                <div className="button-group -full -tall">
                    <div className="form-field -full">
                        <MentionsInput
                            value={mentionText}
                            onChange={(e, value) => setMentionText(value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handlePost();
                            }}
                            style={defaultStyle}
                            allowSpaceInQuery={true}
                            placeholder="Add a comment... (tag with @ — Cmd+Enter to post)"
                        >
                            <Mention
                                trigger="@"
                                data={fetchMentionSuggestions}
                                markup="@[__display__](__id__)"
                                renderSuggestion={(suggestion) =>
                                    suggestion.isCollaborator ? (
                                        <span>{suggestion.display}</span>
                                    ) : (
                                        <span>
                                            {suggestion.display}{" "}
                                            <span className="txt -xsmall -light">— will be invited</span>
                                        </span>
                                    )
                                }
                            />
                        </MentionsInput>
                    </div>
                    <button
                        className={classNames("button -small", {
                            "-primary": mentionText.trim(),
                            "-secondary": !mentionText.trim(),
                        })}
                        disabled={posting}
                        onClick={handlePost}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Activity Tab ─────────────────────────────────────────────────────────────

const ActivityTab = () => {
    const { id: initiativeId } = useParams();

    return (
        <div className="activity-tab">
            <div className="activity-tab__feed">
                <ActivityFeed initiativeId={initiativeId} />
            </div>
        </div>
    );
};

export default ActivityTab;
